import type {ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import {Dialog as DialogPrimitive} from 'radix-ui'
import {Dialog, DialogContent, DialogTitle, DialogTrigger} from '@/components/ui/dialog'
import {ArrowLeftGlyph} from '@/components/ui/icons'
import {Popover, PopoverContent, PopoverTrigger} from '@/components/ui/popover'
import {useIsMobile} from '@/hooks/common/use-media-query'
import {cn} from '@/lib/utils'

/** Below `md` an anchored popover overflows or hides under the virtual keyboard, so mobile opens a
 * full-screen sheet. `trigger` is the bare button: Radix triggers aren't interchangeable. */
export function SearchComboboxShell({
  trigger,
  title,
  open,
  onOpenChange,
  children,
  popoverClassName,
  align,
}: {
  trigger: ReactNode
  title: string
  open: boolean
  onOpenChange: (open: boolean) => void
  children: ReactNode
  popoverClassName?: string
  align?: 'start' | 'center' | 'end'
}) {
  const {t} = useTranslation()
  const isMobile = useIsMobile()

  if (isMobile) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogTrigger asChild>{trigger}</DialogTrigger>
        <DialogContent
          showCloseButton={false}
          className="top-0 left-0 h-dvh max-h-dvh w-screen max-w-none translate-x-0 translate-y-0 grid-rows-[auto_1fr] rounded-none border-0 p-0 gap-0">
          <div className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-2">
            {/* U17: el rótulo dice lo que dibuja la flecha (volver), no «Cerrar». */}
            <DialogPrimitive.Close asChild>
              <button
                type="button"
                aria-label={t('common.back')}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-foreground hover:bg-secondary">
                <ArrowLeftGlyph size={20} />
              </button>
            </DialogPrimitive.Close>
            <DialogTitle className="min-w-0 truncate text-base font-medium text-foreground">{title}</DialogTitle>
          </div>
          {/* Without this wrapper the input takes the `1fr` row and the list falls into an implicit
           * row; one flex column gives the list's `overflow-y-auto` a bounded parent. */}
          <div className="flex min-h-0 flex-col">{children}</div>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange} modal>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align={align} className={cn('w-[var(--radix-popover-trigger-width)] p-0', popoverClassName)}>
        {children}
      </PopoverContent>
    </Popover>
  )
}
